import { useState, useRef } from "react";
import { X, Upload, FileText, Download, AlertCircle, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import Papa from "papaparse";

export default function BulkPatronModal({ onClose, onUpload }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const processFile = (selected) => {
    if (!selected) return;

    // Check CSV mime types or .csv file extension
    const validExtension = selected.name.endsWith(".csv");
    const validTypes = ["text/csv", "application/vnd.ms-excel", "text/plain"];
    if (!validTypes.includes(selected.type) && !validExtension) {
      setErrors([{ row: "-", error: "Invalid file format. Please upload a valid CSV file." }]);
      return;
    }

    setFile(selected);
    setErrors([]);
    setPreview([]);

    Papa.parse(selected, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setPreview(results.data);
      },
      error: (err) => {
        setErrors([{ row: "-", error: `CSV Parse Error: ${err.message}` }]);
      },
    });
  };

  const handleFileChange = (e) => {
    processFile(e.target.files[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    setPreview([]);
    setErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDownloadTemplate = () => {
    const csv = "cardnumber,firstname,surname,title\nP001,John,Doe,Mr\nP002,Jane,Smith,Ms";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "patron_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setIsUploading(true);
      const response = await onUpload(file);
      if (response?.errors?.length > 0) {
        setErrors(response.errors);
      } else {
        setPreview([]);
        setFile(null);
        setErrors([]);
        onClose();
      }
    } catch (err) {
      console.error(err);
      setErrors([{ row: "-", error: "An unexpected network or server error occurred." }]);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-dynamic-title animate-fadeIn">
      <div className="bg-[#1a1613] border border-[#2e2720] rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden transform transition-all">
        
        {/* MODAL HEADER */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
              <Upload className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-stone-100">Bulk Patron Upload</h3>
              <p className="text-xs text-stone-400 mt-0.5">
                Batch import patron records via CSV spreadsheet.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-100 hover:bg-[#2e2720] p-1.5 rounded-xl transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* MODAL BODY */}
        <div className="p-6 space-y-5 text-xs font-semibold">

          {/* TEMPLATE DOWNLOAD BANNER */}
          <div className="flex items-center justify-between bg-[#131110] border border-[#2e2720] rounded-xl p-3.5">
            <div className="flex items-center gap-2.5 text-stone-300">
              <FileText className="w-4 h-4 text-amber-400 shrink-0" />
              <span>Need the standard CSV structure before uploading?</span>
            </div>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#201c17] hover:bg-[#2e2720] border border-[#3e352c] text-amber-400 hover:text-amber-300 rounded-lg transition shrink-0"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Template</span>
            </button>
          </div>

          {/* DRAG & DROP / FILE INPUT ZONE */}
          {!file ? (
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                isDragging
                  ? "border-amber-500 bg-amber-500/10"
                  : "border-[#2e2720] bg-[#131110] hover:border-amber-500/50 hover:bg-[#161311]"
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="w-12 h-12 bg-amber-500/10 border border-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3 text-amber-400">
                <Upload className="w-6 h-6" />
              </div>
              <p className="text-stone-200 font-bold text-xs mb-1">
                Click to browse or drop CSV file here
              </p>
              <p className="text-stone-500 text-[11px]">Only .csv files are supported</p>
            </div>
          ) : (
            /* SELECTED FILE INDICATOR */
            <div className="flex items-center justify-between bg-[#131110] border border-amber-500/30 rounded-xl p-3.5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-500/10 rounded-lg text-amber-400">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-stone-100 font-bold">{file.name}</p>
                  <p className="text-stone-500 text-[11px]">
                    {(file.size / 1024).toFixed(1)} KB • {preview.length} record(s) parsed
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="p-1.5 text-stone-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition"
                title="Remove file"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* PARSED PREVIEW TABLE */}
          {preview.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-stone-400 text-[11px] uppercase tracking-wider font-bold">
                <span>File Content Preview</span>
                <span>{preview.length} items</span>
              </div>
              <div className="border border-[#2e2720] rounded-xl overflow-hidden bg-[#131110] max-h-48 overflow-y-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-[#1a1613] border-b border-[#2e2720] text-stone-400">
                      {Object.keys(preview[0]).map((key) => (
                        <th key={key} className="p-2.5 font-semibold capitalize">
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2e2720] text-stone-200">
                    {preview.slice(0, 10).map((row, idx) => (
                      <tr key={idx} className="hover:bg-[#1a1613]/50 transition-colors">
                        {Object.values(row).map((val, i) => (
                          <td key={i} className="p-2.5 font-mono text-[11px]">
                            {String(val)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.length > 10 && (
                  <div className="p-2 text-center text-[11px] text-stone-500 bg-[#1a1613]/30 border-t border-[#2e2720]">
                    Showing first 10 of {preview.length} records
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ERROR DISPLAY */}
          {errors.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3.5 text-red-400 space-y-1.5">
              <div className="flex items-center gap-2 font-bold text-xs">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>Upload Warnings / Errors:</span>
              </div>
              <ul className="list-disc list-inside space-y-1 text-[11px] text-red-300 max-h-32 overflow-y-auto pl-1">
                {errors.map((err, idx) => (
                  <li key={idx}>
                    <span className="font-semibold">Row {err.row}:</span>{" "}
                    {typeof err.error === "object" ? JSON.stringify(err.error) : err.error}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ACTION BUTTONS */}
          <div className="pt-4 flex items-center justify-end gap-2.5 border-t border-[#2e2720] mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-4 h-9 bg-[#131110] font-mono border border-[#2e2720] text-stone-300 hover:text-stone-100 hover:bg-[#201c17] rounded-xl disabled:opacity-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || isUploading}
              className="inline-flex items-center font-mono gap-2 px-5 h-9 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold rounded-xl shadow disabled:opacity-50 transition"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>Confirm Upload</span>
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}